import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

// Main Plantafel controller - orchestrates the calendar view
export default class extends Controller {
  static targets = ["calendar", "dateDisplay", "viewToggle"]
  static values = {
    currentDate: String,
    viewMode: { type: String, default: "week" },
    verticalGroupBy: { type: String, default: "trade" },
    errorAssign: { type: String, default: "Error assigning resource" },
    errorRemove: { type: String, default: "Error removing resource" },
    errorSave: { type: String, default: "Error saving changes" },
    conflictsDetected: { type: String, default: "Conflicts detected:" },
    contextTask: { type: String, default: "Task" },
    contextAppointment: { type: String, default: "Appointment" },
    contextSickNote: { type: String, default: "Sick note" },
    contextLeaveRequest: { type: String, default: "Leave request" },
    contextView: { type: String, default: "%{label} view" },
    contextEdit: { type: String, default: "%{label} edit" },
  }

  initialize() {
    this.csrfToken = document.head.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
  }

  connect() {
    // Listen for custom events from drag controllers
    this.element.addEventListener("plantafel:resource-dropped", this.handleResourceDrop.bind(this))
    this.element.addEventListener(
      "plantafel:resource-removed",
      this.handleResourceRemove.bind(this),
    )
    this.element.addEventListener("plantafel:task-moved", this.handleTaskMove.bind(this))
  }

  disconnect() {
    this.element.removeEventListener(
      "plantafel:resource-dropped",
      this.handleResourceDrop.bind(this),
    )
    this.element.removeEventListener(
      "plantafel:resource-removed",
      this.handleResourceRemove.bind(this),
    )
    this.element.removeEventListener("plantafel:task-moved", this.handleTaskMove.bind(this))
  }

  // Navigate to previous period
  previousPeriod(event) {
    event.preventDefault()
    const url = event.currentTarget.href || event.currentTarget.dataset.url
    if (url) {
      Turbo.visit(url)
    }
  }

  // Navigate to next period
  nextPeriod(event) {
    event.preventDefault()
    const url = event.currentTarget.href || event.currentTarget.dataset.url
    if (url) {
      Turbo.visit(url)
    }
  }

  // Navigate to today
  goToToday(event) {
    event.preventDefault()
    const url = event.currentTarget.href || event.currentTarget.dataset.url
    if (url) {
      Turbo.visit(url)
    }
  }

  // Handle resource drop on task
  handleResourceDrop(event) {
    const { taskId, date, timespanId, resourceId, resourceType } = event.detail

    this.assignResource(taskId, resourceId, resourceType, date, timespanId)
      .then(async (response) => {
        if (response.ok) {
          const contentType = response.headers.get("Content-Type")
          if (contentType && contentType.includes("text/vnd.turbo-stream.html")) {
            const html = await response.text()
            this.processAssignmentResponse(html, taskId, resourceId, resourceType)
          }
        } else {
          this.showNotification(this.errorAssignValue, "error")
        }
      })
      .catch((error) => {
        Honeybadger.notify(error, { context: { component: "plantafel", action: "assignment" } })
        this.showNotification(this.errorAssignValue, "error")
      })
  }

  // Process assignment response and update all task instances
  processAssignmentResponse(html, _taskId, _resourceId, _resourceType) {
    // Let Turbo handle all streams in the response
    // This includes task badge updates and resource panel updates
    Turbo.renderStreamMessage(html)
  }

  // Handle resource removal from task
  handleResourceRemove(event) {
    const { assignmentId, resourceType } = event.detail

    this.unassignResource(assignmentId, resourceType)
      .then(async (response) => {
        if (response.ok) {
          const contentType = response.headers.get("Content-Type")
          if (contentType && contentType.includes("text/vnd.turbo-stream.html")) {
            const html = await response.text()
            Turbo.renderStreamMessage(html)
          }
        } else {
          this.showNotification(this.errorRemoveValue, "error")
        }
      })
      .catch((error) => {
        Honeybadger.notify(error, { context: { component: "plantafel", action: "unassignment" } })
        this.showNotification(this.errorRemoveValue, "error")
      })
  }

  // API call to assign resource
  assignResource(taskId, resourceId, resourceType, date, timespanId) {
    const url = `/plantafel/tasks/${taskId}/assign_resource`
    const data = {
      resource_id: resourceId,
      resource_type: resourceType,
      date: date,
      timespan_id: timespanId,
      view_mode: this.viewModeValue,
      vertical_group_by: this.verticalGroupByValue,
      view_start_date: this.currentDateValue,
    }

    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
  }

  // API call to unassign resource
  unassignResource(assignmentId, resourceType) {
    const params = new URLSearchParams({
      resource_type: resourceType,
      view_mode: this.viewModeValue,
      view_start_date: this.currentDateValue,
    })
    const url = `/plantafel/assignments/${assignmentId}?${params.toString()}`

    return fetch(url, {
      method: "DELETE",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
    })
  }

  // Handle task move between sites (with optional position change)
  handleTaskMove(event) {
    const { taskId, fromSiteId, toSiteId, startDay, startPart, endDay, endPart, hasPosition } =
      event.detail

    // Build position object if position data is provided
    const position = hasPosition ? { startDay, startPart, endDay, endPart } : null

    this.moveTaskToSite(taskId, toSiteId, position)
      .then(async (response) => {
        if (response.ok) {
          const contentType = response.headers.get("Content-Type")
          if (contentType && contentType.includes("text/vnd.turbo-stream.html")) {
            const html = await response.text()
            Turbo.renderStreamMessage(html)
          }
        } else {
          const errorText = await response.text()
          Honeybadger.notify("Task move failed", {
            context: {
              level: "warning",
              component: "plantafel",
              action: "move_task",
              taskId,
              fromSiteId,
              toSiteId,
              position,
              status: response.status,
              error: errorText,
            },
          })
          this.showNotification(this.errorSaveValue, "error")
        }
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: {
            component: "plantafel",
            action: "move_task",
            taskId,
            fromSiteId,
            toSiteId,
            position,
          },
        })
        this.showNotification(this.errorSaveValue, "error")
      })
  }

  // API call to move task to different site (with optional position change)
  moveTaskToSite(taskId, toSiteId, position = null) {
    const url = `/plantafel/tasks/${taskId}/move_to_site`
    const data = {
      site_id: toSiteId,
      view_mode: this.viewModeValue,
      view_start_date: this.currentDateValue,
    }

    // Include position data for combined site + time repositioning
    if (position) {
      data.task = {
        start_day: position.startDay,
        start_part: position.startPart,
        end_day: position.endDay,
        end_part: position.endPart,
      }
    }

    return fetch(url, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
  }

  // Show notification (using existing notification system if available)
  showNotification(message, type = "info") {
    // Check if there's an existing notification system
    const flashContainer =
      document.getElementById("flash-messages") || document.querySelector("[data-flash-target]")

    if (flashContainer) {
      const alertClass =
        type === "error" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
      const alert = document.createElement("div")
      alert.className = `p-4 mb-4 rounded-lg ${alertClass}`
      alert.innerHTML = `
        <div class="flex items-center">
          <span>${message}</span>
          <button type="button" class="ml-auto" onclick="this.parentElement.parentElement.remove()">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      `
      flashContainer.prepend(alert)

      // Auto-remove after 5 seconds
      setTimeout(() => alert.remove(), 5000)
    } else {
      // Fallback to console
      console.warn(`[${type}] ${message}`)
    }
  }

  // Toggle section visibility (appointments, holidays, tasks)
  toggleSection(event) {
    const section = event.currentTarget.dataset.section
    const content = this.element.querySelector(`[data-section-content="${section}"]`)
    const icon = event.currentTarget.querySelector("svg")

    if (content) {
      content.classList.toggle("hidden")
      if (icon) {
        icon.classList.toggle("rotate-90")
      }
    }
  }
}
